// ═══════════════════════════════════════════════════════════════
// RollCall — Add Personnel (Directory-first flow)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Select, Textarea, Text, Badge,
} from '../../components/gab/GabComponents';
import { IconArrowLeft, IconCheck, IconSearch, IconAddressBook } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useRollCallStore } from '../../store/rollcallStore';
import { ICS_POSITIONS } from '../../types';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

type DirResult = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  department: string;
};

export default function PersonnelCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { personnel, loadPersonnel, createPersonnel } = useRollCallStore();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', agency: '', position: '',
    pin: '', badgeId: '', phone: '', email: '', notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Directory search state
  const [dirQuery, setDirQuery] = useState('');
  const [dirResults, setDirResults] = useState<DirResult[]>([]);
  const [dirSearching, setDirSearching] = useState(false);
  const [selectedFromDir, setSelectedFromDir] = useState<DirResult | null>(null);

  useEffect(() => {
    loadPersonnel();
    // Check for prefill params (from "Enroll" button in PersonnelList)
    const prefillParam = searchParams.get('prefill');
    if (prefillParam) {
      try {
        const prefill = JSON.parse(decodeURIComponent(prefillParam));
        setForm(prev => ({
          ...prev,
          firstName: prefill.firstName || '',
          lastName: prefill.lastName || '',
          email: prefill.email || '',
          phone: prefill.phone || '',
          position: prefill.position || '',
          agency: prefill.agency || '',
        }));
      } catch { /* ignore */ }
    }
  }, []);

  const handleDirSearch = async () => {
    if (!TABLES.people || !dirQuery.trim()) return;
    setDirSearching(true);
    try {
      const FP = FIELDS.people;
      const r = await queryRecords<Record<string, unknown>>(TABLES.people, {
        search: dirQuery.trim(),
        filters: [
          { field: FP.type, operator: 'in', value: 'employee,staff,contractor' },
          { field: FP.status, operator: 'eq', value: 'active' },
        ],
        limit: 10,
      });
      setDirResults(r.records.map(p => ({
        id: p.record_id as number,
        firstName: String(p[FP.firstName] ?? ''),
        lastName: String(p[FP.lastName] ?? ''),
        email: String(p[FP.email] ?? ''),
        phone: String(p[FP.phone] ?? ''),
        title: String(p[FP.title] ?? ''),
        department: String(p[FP.department] ?? ''),
      })));
    } catch (e) {
      console.error('[PersonnelCreate] dir search', e);
    } finally {
      setDirSearching(false);
    }
  };

  const selectDirPerson = (dp: DirResult) => {
    setSelectedFromDir(dp);
    setForm(prev => ({
      ...prev,
      firstName: dp.firstName,
      lastName: dp.lastName,
      email: dp.email,
      phone: dp.phone,
      position: dp.title,
      agency: dp.department,
    }));
    setDirResults([]);
    setDirQuery('');
  };

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.agency.trim()) errs.agency = 'Required';
    if (!form.pin.trim()) errs.pin = 'Required';
    else if (!/^\d{4,6}$/.test(form.pin)) errs.pin = 'PIN must be 4-6 digits';
    else if (personnel.some(p => p.pin === form.pin)) errs.pin = 'PIN already in use';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      notifications.show({ title: 'Error', message: 'Please fix validation errors', color: 'var(--color-error)' });
      return;
    }
    setSaving(true);
    try {
      await createPersonnel({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        agency: form.agency.trim(),
        position: form.position || '',
        pin: form.pin,
        badgeId: form.badgeId.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes.trim(),
      });
      notifications.show({ title: 'Added', message: 'Personnel added to roster', color: 'var(--color-success)' });
      navigate('/rollcall/personnel');
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to add', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Add Personnel</Title>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
      </Group>

      {/* Directory Search */}
      {TABLES.people && (
        <Paper p="md" radius="sm" withBorder mb="md" style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}>
          <Text size="sm" fw={600} mb="xs" style={{ color: '#0369a1' }}>
            <IconAddressBook size={14} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Search People Directory First
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Find an existing directory record to auto-fill the form. This prevents duplicate person records.
          </Text>
          {selectedFromDir && (
            <div style={{ marginBottom: 8, padding: '8px 8px', background: '#dcfce7', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <Badge color="var(--color-success)" size="xs">Linked from Directory</Badge>
              <strong>{selectedFromDir.firstName} {selectedFromDir.lastName}</strong>
              <span style={{ color: '#6b7280' }}>{selectedFromDir.email}</span>
              <button onClick={() => setSelectedFromDir(null)}
                style={{ marginLeft: 'auto', fontSize: 12, color: '#B12525', background: 'none', border: 'none', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          )}
          <Group gap="xs">
            <TextInput
              placeholder="Search by name or email..."
              value={dirQuery}
              onChange={e => setDirQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleDirSearch()}
              size="xs"
              style={{ flex: 1 }}
            />
            <Button size="xs" variant="light" leftSection={<IconSearch size={12} />}
              loading={dirSearching} onClick={handleDirSearch}>
              Search
            </Button>
          </Group>
          {dirResults.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              {dirResults.map(dp => (
                <div key={dp.id}
                  onClick={() => selectDirPerson(dp)}
                  style={{ padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 14 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F8F8F8')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                  <strong>{dp.firstName} {dp.lastName}</strong>
                  {dp.title && <span style={{ color: '#6b7280', marginLeft: 8 }}>{dp.title}</span>}
                  {dp.department && <span style={{ color: '#9ca3af', marginLeft: 8 }}>· {dp.department}</span>}
                  {dp.email && <span style={{ color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>· {dp.email}</span>}
                </div>
              ))}
            </div>
          )}
          {dirResults.length === 0 && dirQuery && !dirSearching && (
            <Text size="xs" c="dimmed" mt="xs">No results — you can still create a new record below.</Text>
          )}
        </Paper>
      )}

      <Paper p="lg" radius="sm" withBorder>
        <Group grow mb="md">
          <TextInput label="First Name" required value={form.firstName} error={errors.firstName}
            onChange={e => update('firstName', e.target.value)} />
          <TextInput label="Last Name" required value={form.lastName} error={errors.lastName}
            onChange={e => update('lastName', e.target.value)} />
        </Group>

        <TextInput label="Agency / Organization" required value={form.agency} error={errors.agency}
          onChange={e => update('agency', e.target.value)} mb="md" />

        <Select label="ICS Position" placeholder="Select position" data={ICS_POSITIONS} clearable
          value={form.position || null} onChange={v => update('position', v ?? '')} mb="md" />

        <Group grow mb="md">
          <TextInput label="PIN" required placeholder="4-6 digits" value={form.pin} error={errors.pin}
            onChange={e => update('pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
            description="Unique numeric PIN for kiosk check-in" />
          <TextInput label="Badge ID" placeholder="QR or barcode value" value={form.badgeId}
            onChange={e => update('badgeId', e.target.value)}
            description="Optional — for badge scanning" />
        </Group>

        <Group grow mb="md">
          <TextInput label="Phone" value={form.phone} onChange={e => update('phone', e.target.value)} />
          <TextInput label="Email" value={form.email} onChange={e => update('email', e.target.value)} />
        </Group>

        <Textarea label="Notes" value={form.notes} onChange={e => update('notes', e.target.value)} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={14} />}>
            Add Personnel
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
